import {
  buffers,
  type Channel,
} from 'redux-saga';
import {
  actionChannel,
  call,
  put,
  take,
  takeLeading,
} from 'redux-saga/effects';
import { select } from '~/_shared/utils/saga/effects';
import { DemographicsDataProviders } from '~/store/demographics/demographics.types';
import {
  demographicsListPremiumIsLoadedSelector, demographicsListPremiumIsLoadingSelector,
} from '~/store/demographics/premiumDemographics/demographicsListPremium.selectors';
import { clientIdSelector } from '../../selectors/useClientIdSelector';
import { createDemographicsRegionFromResponse } from '../demographics.factory';
import {
  type DemographicsPremiumListResponse, getDemographicsListAgs,
} from '../demographics.repository';
import {
  demographicsListPremiumFetchDataError,
  demographicsListPremiumFetchDataRequest,
  demographicsListPremiumFetchDataStart,
  demographicsListPremiumFetchDataSuccess,
} from './demographicsListPremium.actionCreators';

export function* demographicsListPremiumSagas() {
  yield takeLeading(demographicsListPremiumFetchDataRequest, onDemographicsListPremiumFetchDataRequest);
}

function* onDemographicsListPremiumFetchDataRequest() {
  const clientId: number | null = yield select<number | null>(clientIdSelector);
  const isDemographicsListPremiumFetched: boolean = yield select<boolean>(demographicsListPremiumIsLoadedSelector);
  const isDemographicsListPremiumLoading: boolean = yield select<boolean>(demographicsListPremiumIsLoadingSelector);

  if (clientId === null || isDemographicsListPremiumFetched || isDemographicsListPremiumLoading) {
    return;
  }

  yield call(fetchDemographicsListPremium);
}

function* fetchDemographicsListPremium() {
  const clientId: number | null = yield select<number | null>(clientIdSelector);

  if (clientId === null) {
    return;
  }

  yield put(demographicsListPremiumFetchDataStart());

  try {
    const agsResponse: DemographicsPremiumListResponse = yield call(getDemographicsListAgs, clientId);

    const processedAgsDemographics = agsResponse.data.map(region => createDemographicsRegionFromResponse(region));

    yield put(demographicsListPremiumFetchDataSuccess({
      [DemographicsDataProviders.AGS]: processedAgsDemographics,
    }));
  }
  catch (e) {
    console.error('Premium demographics fetch error:', e);
    yield put(demographicsListPremiumFetchDataError());
  }
}

export function* waitForPremiumDemographicsReady() {
  let isReady: boolean = yield select<boolean>(demographicsListPremiumIsLoadedSelector);
  if (isReady) {
    return;
  }

  const premiumDemographicsChannel: Channel<Action> = yield actionChannel(
    [demographicsListPremiumFetchDataSuccess, demographicsListPremiumFetchDataError],
    buffers.sliding(1)
  );

  while (!isReady) {
    yield take(premiumDemographicsChannel);
    isReady = yield select<boolean>(demographicsListPremiumIsLoadedSelector);
  }
}
